/**
 * explore-navigation-state — sessionStorage-backed state that survives a full
 * unmount of HomePage.
 *
 * HomePage's active section (Wallpapers / Live / Ringtones / By Users) and its
 * active top-level tab (Explore / Library / Favorites / Sales / Profile) are
 * plain React state. Tapping a creator's name from a fullview (wallpaper
 * viewer, ringtone full-screen player, subscriptions dialog, search results,
 * or the "By Users" grid) navigates to /user/:identifier via react-router,
 * which is a real route change — HomePage unmounts entirely. Pressing back
 * (in-app chevron or Android hardware back) remounts HomePage fresh, so any
 * state tracked only in component state resets to its default (Explore /
 * Wallpapers). This module persists both values across that unmount/remount
 * cycle, the same way search state already does.
 *
 * Shared by all three parallel media sections and all top-level tabs — one
 * storage key per concern covers every section/tab, so there is nothing to
 * duplicate per Library/Favorites/Explore entry point.
 */

const ACTIVE_SECTION_KEY = 'seekscape-active-section';
const ACTIVE_TAB_KEY = 'seekscape-active-tab';

/** Read the last-active section index, clamped to a valid range. Defaults to 0 (Wallpapers). */
export function getStoredActiveSection(sectionCount: number): number {
  try {
    const stored = sessionStorage.getItem(ACTIVE_SECTION_KEY);
    if (stored !== null) {
      const idx = Number(stored);
      if (Number.isInteger(idx) && idx >= 0 && idx < sectionCount) return idx;
    }
  } catch { /* ignore */ }
  return 0;
}

/** Persist the active section index whenever it changes. */
export function setStoredActiveSection(index: number): void {
  try {
    sessionStorage.setItem(ACTIVE_SECTION_KEY, String(index));
  } catch { /* ignore */ }
}

/**
 * Valid top-level tab ids, duplicated here (instead of importing the `Tab`
 * type from CategoryPageShell) so this module has no component dependency —
 * it's plain storage plumbing consumed by HomePage.
 */
const VALID_TABS = ['home', 'favorites', 'library', 'sales', 'menu'] as const;
type StoredTab = (typeof VALID_TABS)[number];

/** Read the last-active top-level tab (Explore/Library/Favorites/Sales/Profile). Defaults to 'home'. */
export function getStoredActiveTab(): StoredTab {
  try {
    const stored = sessionStorage.getItem(ACTIVE_TAB_KEY);
    if (stored && (VALID_TABS as readonly string[]).includes(stored)) return stored as StoredTab;
  } catch { /* ignore */ }
  return 'home';
}

/** Persist the active top-level tab whenever it changes. */
export function setStoredActiveTab(tab: string): void {
  try {
    sessionStorage.setItem(ACTIVE_TAB_KEY, tab);
  } catch { /* ignore */ }
}
